import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Bell, UserPlus, Heart, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TYPE_ICON = {
  new_follower: <Heart size={14} className="text-bx-magenta" />,
  close_friend_request: <UserPlus size={14} className="text-bx-cyan" />,
  close_friend_accepted: <Check size={14} className="text-bx-volt" />,
  close_friend_declined: <X size={14} className="text-white/40" />,
};

const TYPE_TEXT = {
  new_follower: (d) => `@${d.follower_username} started following ${d.bubble_name}`,
  close_friend_request: (d) => `@${d.requester_username} wants to be close with ${d.bubble_name}`,
  close_friend_accepted: (d) => `Your close request to ${d.bubble_name} was accepted`,
  close_friend_declined: (d) => `Your close request to ${d.bubble_name} was declined`,
};

const timeAgo = (iso) => {
  const secs = (Date.now() - new Date(iso)) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

export default function NotificationsPanel() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/me/notifications');
      setNotifs(data || []);
    } catch (_) {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unread = notifs.filter((n) => !n.read).length;

  const openPanel = async () => {
    setOpen((o) => !o);
    if (!open && unread > 0) {
      await api.post('/me/notifications/read-all').catch(() => {});
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  const respondClose = async (connId, action, notifId) => {
    setBusy(true);
    try {
      await api.post(`/close-connections/${connId}/respond`, { action });
      toast.success(action === 'accept' ? 'Inner circle opened' : 'Declined');
      setNotifs((prev) => prev.filter((n) => n.id !== notifId));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'could not respond');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notifications-panel">
      <button
        onClick={openPanel}
        data-testid="notifications-bell"
        className="relative p-2 rounded-full hover:bg-white/8 transition"
      >
        <Bell size={20} className="text-white/70" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-bx-magenta text-[9px] font-bold grid place-items-center text-white"
                data-testid="notif-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-12 w-80 z-50 rounded-2xl border border-white/10 overflow-hidden"
            style={{ background: 'rgba(12,14,36,0.96)', backdropFilter: 'blur(20px)' }}
            data-testid="notifications-dropdown"
          >
            <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-white/60">Notifications</span>
              {notifs.length > 0 && (
                <button onClick={() => { api.post('/me/notifications/read-all'); setNotifs([]); }}
                        className="text-[10px] text-white/40 hover:text-white/70">clear all</button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="py-10 text-center text-white/40 text-sm">all caught up</div>
              ) : (
                notifs.map((n) => (
                  <div key={n.id}
                       className={`px-4 py-3 border-b border-white/5 last:border-b-0 ${!n.read ? 'bg-white/[0.03]' : ''}`}
                       data-testid={`notif-item-${n.type}`}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0">{TYPE_ICON[n.type] || <Bell size={14} />}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/85 leading-snug">
                          {(TYPE_TEXT[n.type] || ((d) => n.type))(n.data || {})}
                        </p>
                        {n.data?.message && (
                          <p className="text-xs text-white/50 mt-0.5 italic">"{n.data.message}"</p>
                        )}
                        <p className="text-[10px] text-white/40 mt-1">{timeAgo(n.created_at)}</p>
                        {n.type === 'close_friend_request' && n.data?.connection_id && (
                          <div className="flex gap-2 mt-2">
                            <button disabled={busy}
                                    onClick={() => respondClose(n.data.connection_id, 'accept', n.id)}
                                    data-testid="notif-accept-close"
                                    className="px-3 py-1 rounded-full bg-bx-cyan text-black text-xs font-bold hover:opacity-90 disabled:opacity-50">
                              Accept
                            </button>
                            <button disabled={busy}
                                    onClick={() => respondClose(n.data.connection_id, 'decline', n.id)}
                                    data-testid="notif-decline-close"
                                    className="px-3 py-1 rounded-full glass-hi text-xs font-bold hover:bg-white/10">
                              Decline
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
